<script setup lang="ts">
import { diContainer } from '@/coreContainer/container';
import { IOrderGroup } from '@/coreDomainsModule/entities/IOrderGroup';
import { Order } from '@/coreDomainsModule/entities/order';
import { initPagingOptions } from '@/coreDomainsModule/paging';
import { DateService } from '@/coreDomainsModule/services/dateService';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import { LoadingStatus, loadingStatusHelpers } from '@/corePagesModule/components/ezTable/types';
import { i18n } from '@/i18n';
import { OrderGroupService } from '@/orderTableModule/domains/service/orderGroupService';
import SvgLoader from '@/utils/SvgLoader.vue';
import { computed, ref, watch } from 'vue';

const t = i18n.t;

const gridTemplateColumns: string[] = ['124px', '124px', '124px', '124px', '124px', '124px', '75px', '124px'];

const props = withDefaults(
  defineProps<{
    changedETA: number;
    changeOrderCount: number;
    changeOrderWithGroupCount: number;
    targetOrderGroups: IOrderGroup[];
  }>(),
  {}
);

const selectedOrderGroupId = ref<string | undefined>(undefined);
const items = ref<Order[]>([]);
const loadingStatus = ref<LoadingStatus>({ type: 'loading' });

const selectedOrderGroup = computed(() =>
  selectedOrderGroupId.value ? props.targetOrderGroups.find((group) => group.orderGroupId === selectedOrderGroupId.value) : undefined
);

const additionalChangeOrderCount = computed(
  () => props.targetOrderGroups.reduce((acc, cur) => (acc += cur.linkedOrderCount), 0) - props.changeOrderWithGroupCount
);

/**
 * fetchとinitialization
 */
const fetchAndInitItem = async () => {
  if (!selectedOrderGroupId.value) return;
  const { loadingStatus: fetchLoadingStatus, items: fetchItems } = await _fetchOrderLinkedTargetOrderGroup(selectedOrderGroupId.value);
  loadingStatus.value = fetchLoadingStatus;
  if (!loadingStatusHelpers.isError(fetchLoadingStatus) && fetchItems) {
    items.value = fetchItems;
  }
};

/**
 * 対象の発注グループに紐づく発注を取得する
 * @param orderGroupId - 対象の発注グループID
 */
const _fetchOrderLinkedTargetOrderGroup = async (orderGroupId: string): Promise<{ loadingStatus: LoadingStatus; items?: Order[] }> => {
  if (!selectedOrderGroupId.value) return { loadingStatus: { type: 'error' }, items: undefined };
  const useFilterItems = [OrderGroupService.getFilterContentItem(selectedOrderGroupId.value)];
  const result = await diContainer.getUnorderedArrivalUseCase.Execute(false, initPagingOptions(100), 'orderId', true, useFilterItems, {
    ApprovedAwaitingDeliveryDate: true,
    QuantityConfirmed: true,
    DeliveryDateConfirmed: true,
    PartialDelivery: true,
    Confirmed: true,
    Delivered: true,
    DeliveryDelayed: true,
    ApprovalRequested: false,
  });
  let loadingStatus: LoadingStatus = { type: 'loading' };
  switch (result.status.type) {
    case 'success':
      if ((result.status.data.pagingOptions?.returnCount ?? 0) === 0) {
        loadingStatus = { type: 'zero-match' };
      } else {
        loadingStatus = { type: 'success' };
      }
      return {
        loadingStatus,
        items: result.status.data.result,
      };
    case 'error':
      loadingStatus = {
        type: 'error',
        errorMessage: '取得に失敗しました',
      };
      return {
        loadingStatus,
      };
  }
};

/**
 * 積載率を計算する
 * @param totalAmount - 合計発注数
 * @param upperLimitQuantity - 上限発注数
 */
const getLoadFactor = (totalAmount?: number, upperLimitQuantity?: number) => {
  console.log(totalAmount, upperLimitQuantity);
  if (!totalAmount || !upperLimitQuantity) return '0';
  const loadFactorValue = (totalAmount / upperLimitQuantity) * 100;
  return loadFactorValue.roundToDecimal(1);
};

/**
 * 発注グループを選択した際の挙動
 * @param selected - a
 */
const selectOrderGroup = async (selected: string) => {
  selectedOrderGroupId.value = selected;
  await fetchAndInitItem();
};

watch(
  () => props.targetOrderGroups,
  async () => {
    selectedOrderGroupId.value = props.targetOrderGroups[0]?.orderGroupId;
    await fetchAndInitItem();
  },
  { immediate: true }
);
</script>
<template>
  <div class="bulk-change-eta">
    <div class="section">
      <h2>{{ t('変更情報') }}</h2>
      <div class="info">
        <div class="info__item">
          <div class="info__label">{{ t('納期') }}</div>
          <div class="info__body">
            <SvgLoader name="calendar_today_gray2x" />
            <div class="info__value">
              {{ DateService.convertMSUnixTimeToYYYYMMDDSlash(props.changedETA) }}
            </div>
          </div>
        </div>
        <div class="info__item">
          <div class="info__label">{{ t('選択されている発注') }}</div>
          <div class="info__value">
            <span class="info__value--big">{{ props.changeOrderCount }}</span>
            <span class="info__value--unit">{{ t('件') }}</span>
          </div>
        </div>
        <div class="info__item">
          <div class="info__label">{{ t('紐づいているグループ') }}</div>
          <div class="info__value">
            <span class="info__value--big">{{ props.targetOrderGroups.length }}</span>
            <span class="info__value--unit">{{ t('件') }}</span>
          </div>
        </div>
        <div class="info__item">
          <div class="info__label">{{ t('追加で変更される発注') }}</div>
          <div class="info__value">
            <span class="info__value--big">{{ additionalChangeOrderCount }}</span>
            <span class="info__value--unit">{{ t('件') }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="section">
      <h2>{{ t('グループ情報') }}</h2>
      <div class="group">
        <button
          v-for="(orderGroup, index) in props.targetOrderGroups"
          :key="index"
          class="group__card"
          :class="{ group__card__active: selectedOrderGroupId === orderGroup.orderGroupId }"
          @click="() => selectOrderGroup(orderGroup.orderGroupId)"
        >
          <div class="group__name">{{ orderGroup.orderGroupName }}</div>
          <div class="group__eta">
            {{ `${t('納期')}: ${orderGroup.eta ? DateService.convertMSUnixTimeToYYYYMMDDSlash(orderGroup.eta) : '--'}` }}
          </div>
        </button>
      </div>
    </div>
    <div class="section">
      <h2>{{ t('選択した発注グループの詳細') }}</h2>
      <div class="selected">
        <div class="detail">
          <div class="detail__item">
            <div class="detail__label">
              {{ t('発注グループ名') }}
            </div>
            <div class="detail__value">
              {{ selectedOrderGroup?.orderGroupName }}
            </div>
          </div>
          <div class="detail__item">
            <div class="detail__label">
              {{ t('納期') }}
            </div>
            <div class="detail__value">
              {{ selectedOrderGroup?.eta ? DateService.convertMSUnixTimeToYYYYMMDDSlash(selectedOrderGroup?.eta) : '--' }}
            </div>
          </div>
          <div class="detail__item">
            <div class="detail__label">
              {{ t('発注件数') }}
            </div>
            <div class="detail__value">
              {{ selectedOrderGroup?.linkedOrderCount }}
            </div>
          </div>
          <div class="detail__item">
            <div class="detail__label">
              {{ t('上限数量') }}
            </div>
            <div class="detail__value">
              {{ selectedOrderGroup?.upperLimitQuantity?.addComma() }}
            </div>
          </div>
          <div class="detail__item">
            <div class="detail__label">
              {{ t('発注合計数量') }}
            </div>
            <div class="detail__value">
              {{ selectedOrderGroup?.totalLinkedOrderAmount?.addComma() }}
            </div>
          </div>
          <div class="detail__item">
            <div class="detail__label">
              {{ t('積載率') }}
            </div>
            <div class="detail__value">
              {{ `${getLoadFactor(selectedOrderGroup?.totalLinkedOrderAmount, selectedOrderGroup?.upperLimitQuantity)}%` }}
            </div>
          </div>
        </div>
        <div class="table">
          <h3>{{ t('グループに含まれている発注情報') }}</h3>
          <EzTable
            :grid-template-columns="gridTemplateColumns"
            sticky-header
            max-height="300px"
            :loading-status="loadingStatus"
            @retry="fetchAndInitItem"
          >
            <template #header>
              <EzHeader class="table__header">
                {{ t('置場コード') }}
              </EzHeader>
              <EzHeader class="table__header">
                {{ t('置場名') }}
              </EzHeader>
              <EzHeader class="table__header">
                {{ t('仕入先コード') }}
              </EzHeader>
              <EzHeader class="table__header">
                {{ t('仕入先名') }}
              </EzHeader>
              <EzHeader class="table__header">
                {{ t('商品コード') }}
              </EzHeader>
              <EzHeader class="table__header">
                {{ t('商品名') }}
              </EzHeader>
              <EzHeader class="table__header">
                {{ t('容量') }}
              </EzHeader>
              <EzHeader class="table__header">
                {{ t('発注数量') }}
              </EzHeader>
            </template>
            <template #body>
              <EzTableRow
                v-for="(order, index) in items"
                :id="`orders-row-${index}`"
                :key="order.orderId"
                :grid-template-columns="gridTemplateColumns"
                class="table__row"
              >
                <EzCell class="table__data">
                  {{ order.warehouseNumber }}
                </EzCell>
                <EzCell class="table__data">
                  {{ order.warehouse }}
                </EzCell>
                <EzCell class="table__data">
                  {{ order.supplierNumber }}
                </EzCell>
                <EzCell class="table__data">
                  {{ order.supplierName }}
                </EzCell>
                <EzCell class="table__data">
                  {{ order.businessNumber }}
                </EzCell>
                <EzCell class="table__data">
                  {{ order.businessName }}
                </EzCell>
                <EzCell class="table__data">
                  {{ order.package }}
                </EzCell>
                <EzCell class="table__data">
                  {{ order.orderAmount.addComma() }}
                </EzCell>
              </EzTableRow>
            </template>
          </EzTable>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.bulk-change-eta {
  padding: 16px;
  display: grid;
  gap: 16px;
  grid-template-columns: 100%;
  grid-template-rows: repeat(4, auto);
  * {
    line-height: 1;
    &:not(.material-icons) {
      font-family: 'Noto Sans JP';
    }
  }
  border-radius: 8px;
  background-color: $semanticPrimarySubdued;

  h2 {
    font-weight: 700;
    font-size: 18px;
  }
}
.section {
  display: grid;
  gap: 12px;
}

.info {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  justify-items: center;

  &__item {
    display: grid;
    width: 80%;
    gap: 8px;
    justify-items: center;
  }
  &__body {
    width: 100%;
    padding: 6px;
    background-color: white;
    border: 1px solid $borderDefault;
    border-radius: 4px;

    display: grid;
    grid-template-columns: max-content max-content;
    justify-content: center;
    align-items: center;
    gap: 10px;
  }
  &__label {
    font-size: 16px;
    color: $textDefault;
  }
  &__value {
    &--big {
      font-size: 24px;
      font-weight: 700;
      padding-right: 4px;
    }
    &--unit {
      color: $textSecondary;
    }
  }
}
.group {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;

  &__card {
    display: grid;
    box-sizing: content-box;
    background-color: $semanticWhite;
    padding: 8px;
    border-radius: 8px;
    gap: 4px;
    box-shadow: 0 0 0 1px $borderDefault;

    &__active {
      box-shadow: 0 0 0 3px $borderActive;
    }
  }

  &__name {
    font-size: 16px;
    font-weight: 700;
  }
}

.selected {
  display: grid;
  gap: 16px;
}

.detail {
  display: grid;
  grid-template-columns: 1fr repeat(4, 16%) 80px;
  align-items: center;
  padding: 0 32px;

  &__item {
    display: grid;
    justify-items: center;
    gap: 8px;
    * {
      width: fit-content;
      height: fit-content;
    }
  }
  &__label {
    color: $textSecondary;
  }
  &__value {
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    &--error {
      color: $semanticError;
    }
  }
}

.table {
  display: grid;
  gap: 8px;
  h3 {
    font-weight: 700;
    color: $textSecondary;
  }
  grid-template-rows: max-content;
  height: 310px;
  &__header {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 700;
    line-height: 14px;
    color: $textDefault;
    padding: 11px 0;
    white-space: nowrap;
  }
  &__data {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 14px;
    color: $textSecondary;
    padding: 8px 0;
  }
}
</style>
